import { AdvancedFilterData } from 'components/filter/advancedFilter/FilterFormController';
import { defaultDateRange, DAILY_PRESET_REGISTRY } from 'components/filter/input/DateRangePicker/presets';
import { defaultCreationDateRange } from 'components/filter/modules/article-visibility/defaults';
import { DateValue } from 'components/filter/input/DateRangePicker/types';
import { isValid, parseISO } from 'date-fns';
import { PartialFilterData } from 'models/filterSet';
import { getComparisonRange } from 'components/filter/input/DateRangePicker/utils/rangeDisplay';

// Utilities for managing filter defaults, serialization, and comparison
const stripDatesForPresets = (dateValue: DateValue): DateValue => {
  const cleaned = { ...dateValue };

  if (cleaned.range?.presetValue && cleaned.range.presetValue !== 'custom') {
    const newRange: Record<string, unknown> = {
      presetValue: cleaned.range.presetValue,
    };

    if (cleaned.range.presetLabel) {
      newRange.presetLabel = cleaned.range.presetLabel;
    }

    // Handle 'default' property that exists at runtime but not in DateRange type
    const rangeWithDefault = cleaned.range as Record<string, unknown>;
    if (rangeWithDefault.default !== undefined) {
      newRange.default = rangeWithDefault.default;
    }

    cleaned.range = newRange;
  } else if (cleaned.range?.presetValue === 'custom') {
    const newRange: Record<string, unknown> = {
      presetValue: cleaned.range.presetValue,
    };

    if (cleaned.range.from) {
      newRange.from = cleaned.range.from;
    }
    if (cleaned.range.to) {
      newRange.to = cleaned.range.to;
    }
    if (cleaned.range.presetLabel) {
      newRange.presetLabel = cleaned.range.presetLabel;
    }

    cleaned.range = newRange;
  }

  if (cleaned.comparison?.presetValue && cleaned.comparison.presetValue !== 'no_comparison') {
    const newComparison: Record<string, unknown> = {
      presetValue: cleaned.comparison.presetValue,
    };

    if (cleaned.comparison.presetValue === 'custom') {
      if (cleaned.comparison.from) {
        newComparison.from = cleaned.comparison.from;
      }
      if (cleaned.comparison.to) {
        newComparison.to = cleaned.comparison.to;
      }
    }

    cleaned.comparison = newComparison;
  }

  return cleaned;
};

const recalculatePresetDates = (dateValue: DateValue): DateValue => {
  const converted = { ...dateValue };

  if (converted.range?.presetValue && converted.range.presetValue !== 'custom') {
    const today = new Date();
    const def = DAILY_PRESET_REGISTRY[converted.range.presetValue as keyof typeof DAILY_PRESET_REGISTRY];
    const freshRange = def?.calculate(today);

    if (freshRange) {
      converted.range = {
        ...converted.range,
        from: freshRange.from,
        to: freshRange.to,
      };

      if (
        freshRange &&
        converted.comparison?.presetValue &&
        converted.comparison.presetValue !== 'no_comparison' &&
        converted.comparison.presetValue !== 'custom'
      ) {
        const freshComparison = getComparisonRange(freshRange.from, freshRange.to, converted.comparison.presetValue);

        if (freshComparison.from && freshComparison.to) {
          converted.comparison = {
            ...converted.comparison,
            from: freshComparison.from,
            to: freshComparison.to,
          };
        }
      }
    }
  }

  return converted;
};

export const convertDateStringsToObjects = (dateValue: DateValue): DateValue => {
  const converted = { ...dateValue };

  if (converted.range) {
    converted.range = { ...converted.range };
    if (typeof converted.range.from === 'string') {
      converted.range.from = parseISO(converted.range.from);
    }
    if (typeof converted.range.to === 'string') {
      converted.range.to = parseISO(converted.range.to);
    }
  }

  if (converted.comparison) {
    converted.comparison = { ...converted.comparison };
    if (typeof converted.comparison.from === 'string') {
      converted.comparison.from = parseISO(converted.comparison.from);
    }
    if (typeof converted.comparison.to === 'string') {
      converted.comparison.to = parseISO(converted.comparison.to);
    }
  }

  return recalculatePresetDates(converted);
};

const validateDateValue = (dateValue: unknown): dateValue is DateValue => {
  if (!dateValue || typeof dateValue !== 'object') return false;

  const value = dateValue as Record<string, unknown>;

  const isValidDateValue = (val: unknown): boolean => {
    if (!val) return true;
    if (val instanceof Date) return isValid(val);
    if (typeof val === 'string') {
      const parsed = new Date(val);
      return isValid(parsed);
    }
    return false;
  };

  if (value.range && typeof value.range === 'object') {
    const range = value.range as Record<string, unknown>;
    if (!isValidDateValue(range.from) || !isValidDateValue(range.to)) {
      return false;
    }
  }

  if (value.comparison && typeof value.comparison === 'object') {
    const comparison = value.comparison as Record<string, unknown>;
    if (!isValidDateValue(comparison.from) || !isValidDateValue(comparison.to)) {
      return false;
    }
  }

  return true;
};

export const getDefaultFilterValues = (): AdvancedFilterData => ({
  selectedDateRange: defaultDateRange(),
  selectedCreationDateRange: defaultCreationDateRange(),
  selectedBrands: [],
  selectedMarketplaces: [],
  selectedFulfillmentMethods: [],
  selectedCategories: [],
  selectedCountries: [],
  selectedProducts: [],
  selectedGenders: [],
  selectedAgeGroups: [],
  selectedSeasons: [],
  selectedColors: [],
  selectedSizes: [],
  selectedBenchmark: 'all',
});

const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;

  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  if (typeof obj1 !== 'object') return obj1 === obj2;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false;
  }

  // Arrays: compare regardless of order
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;

    return (
      obj1.every(item1 => obj2.some(item2 => deepEqual(item1, item2))) &&
      obj2.every(item2 => obj1.some(item1 => deepEqual(item1, item2)))
    );
  }

  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => {
    if (!keys2.includes(key)) return false;
    const val1 = (obj1 as Record<string, unknown>)[key];
    const val2 = (obj2 as Record<string, unknown>)[key];
    return deepEqual(val1, val2);
  });
};

const isDefaultDateValue = (dateValue: unknown): boolean => {
  if (!dateValue || typeof dateValue !== 'object') return false;

  const value = dateValue as Record<string, unknown>;

  if (value.range && typeof value.range === 'object') {
    const range = value.range as Record<string, unknown>;
    return range.default === true;
  }

  return false;
};
// Special comparison for default date values to ensure consistency
const deepEqualWithDefaultDateHandling = (obj1: unknown, obj2: unknown): boolean => {
  if (obj1 && typeof obj1 === 'object' && obj2 && typeof obj2 === 'object') {
    const value1 = obj1 as Record<string, unknown>;
    const value2 = obj2 as Record<string, unknown>;

    if (value1.range && typeof value1.range === 'object' && value2.range && typeof value2.range === 'object') {
      const range1 = value1.range as Record<string, unknown>;
      const range2 = value2.range as Record<string, unknown>;

      if (isDefaultDateValue(obj1) && isDefaultDateValue(obj2)) {
        if (range1.presetValue === 'NO_SELECTION' && range2.presetValue === 'NO_SELECTION') {
          return true;
        }
        if (range1.presetValue && range2.presetValue) {
          const presetMatches = range1.presetValue === range2.presetValue;

          let comparisonMatches = true;
          if (value1.comparison || value2.comparison) {
            const comp1 = value1.comparison as Record<string, unknown> | undefined;
            const comp2 = value2.comparison as Record<string, unknown> | undefined;

            if (!comp1 && !comp2) {
              comparisonMatches = true;
            } else if (!comp1 || !comp2) {
              const existingComp = comp1 ?? comp2;
              const defaults = getDefaultFilterValues();
              const defaultComp = defaults.selectedDateRange.comparison;
              comparisonMatches = deepEqual(existingComp, defaultComp);
            } else if (comp1.presetValue && comp2.presetValue) {
              if (comp1.presetValue !== 'custom' && comp2.presetValue !== 'custom') {
                comparisonMatches = comp1.presetValue === comp2.presetValue;
              } else if (comp1.presetValue === 'custom' && comp2.presetValue === 'custom') {
                comparisonMatches = deepEqual(comp1, comp2);
              } else {
                comparisonMatches = false;
              }
            } else {
              comparisonMatches = deepEqual(comp1, comp2);
            }
          }

          return presetMatches && comparisonMatches;
        }
      }

      let rangeMatches;
      if (range1.presetValue && range2.presetValue) {
        if (range1.presetValue !== 'custom' && range2.presetValue !== 'custom') {
          rangeMatches = range1.presetValue === range2.presetValue;
        } else if (range1.presetValue === 'custom' && range2.presetValue === 'custom') {
          rangeMatches = deepEqual(range1, range2);
        } else {
          rangeMatches = false;
        }
      } else {
        rangeMatches = deepEqual(range1, range2);
      }

      let comparisonMatches = true;
      if (value1.comparison || value2.comparison) {
        const comp1 = value1.comparison as Record<string, unknown> | undefined;
        const comp2 = value2.comparison as Record<string, unknown> | undefined;

        if (!comp1 && !comp2) {
          comparisonMatches = true;
        } else if (!comp1 || !comp2) {
          const existingComp = comp1 ?? comp2;
          const defaults = getDefaultFilterValues();
          const defaultComp = defaults.selectedDateRange.comparison;
          comparisonMatches = deepEqual(existingComp, defaultComp);
        } else if (comp1.presetValue && comp2.presetValue) {
          if (comp1.presetValue !== 'custom' && comp2.presetValue !== 'custom') {
            comparisonMatches = comp1.presetValue === comp2.presetValue;
          } else if (comp1.presetValue === 'custom' && comp2.presetValue === 'custom') {
            comparisonMatches = deepEqual(comp1, comp2);
          } else {
            comparisonMatches = false;
          }
        } else {
          comparisonMatches = deepEqual(comp1, comp2);
        }
      }

      return rangeMatches && comparisonMatches;
    }
  }

  return deepEqual(obj1, obj2);
};

export const extractChangedFilters = (filterValues: AdvancedFilterData): PartialFilterData => {
  const defaults = getDefaultFilterValues();
  const changedFilters: PartialFilterData = {};

  (Object.keys(filterValues) as (keyof AdvancedFilterData)[]).forEach(key => {
    const currentValue = filterValues[key];
    const defaultValue = defaults[key];

    if ((key === 'selectedDateRange' || key === 'selectedCreationDateRange') && currentValue) {
      if (!validateDateValue(currentValue)) {
        console.warn(`Invalid date value for ${key}:`, currentValue);
        return;
      }
    }

    if (!deepEqualWithDefaultDateHandling(currentValue, defaultValue)) {
      if (key === 'selectedDateRange' || key === 'selectedCreationDateRange') {
        changedFilters[key] = stripDatesForPresets(currentValue as DateValue);
      } else if (
        key === 'selectedBrands' ||
        key === 'selectedMarketplaces' ||
        key === 'selectedFulfillmentMethods' ||
        key === 'selectedCategories' ||
        key === 'selectedProducts' ||
        key === 'selectedGenders' ||
        key === 'selectedAgeGroups' ||
        key === 'selectedSeasons' ||
        key === 'selectedColors' ||
        key === 'selectedSizes'
      ) {
        changedFilters[key] = currentValue as string[];
      } else if (key === 'selectedCountries') {
        changedFilters[key] = currentValue as typeof filterValues.selectedCountries;
      } else if (key === 'selectedBenchmark') {
        changedFilters[key] = currentValue as string;
      }
    }
  });

  return changedFilters;
};

export const mergeWithCurrentFilters = (
  currentValues: AdvancedFilterData,
  savedFilters: PartialFilterData
): AdvancedFilterData => {
  const mergedValues = { ...currentValues };

  // Validate and merge each saved filter value
  Object.entries(savedFilters).forEach(([key, value]) => {
    // Special handling for date values
    if ((key === 'selectedDateRange' || key === 'selectedCreationDateRange') && value) {
      if (!validateDateValue(value)) {
        console.warn(`Skipping invalid saved date value for ${key}:`, value);
        return; // Skip invalid saved date values
      }

      // Convert date strings to Date objects using the exported helper
      (mergedValues as Record<string, unknown>)[key] = convertDateStringsToObjects(value);
      return;
    }

    // Type-safe assignment without any cast
    const typedKey = key as keyof AdvancedFilterData;
    if (value !== undefined) {
      (mergedValues as Record<string, unknown>)[typedKey] = value;
    }
  });

  return mergedValues;
};

export const arePartialFiltersEqual = (filters1: PartialFilterData, filters2: PartialFilterData): boolean => {
  const keys1 = Object.keys(filters1);
  const keys2 = Object.keys(filters2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => {
    if (!keys2.includes(key)) return false;
    return deepEqual(filters1[key as keyof PartialFilterData], filters2[key as keyof PartialFilterData]);
  });
};

export const doesCurrentStateMatchFilterSet = (
  currentValues: AdvancedFilterData,
  savedFilters: PartialFilterData
): boolean => {
  const defaults = getDefaultFilterValues();

  return (Object.keys(defaults) as (keyof AdvancedFilterData)[]).every(key => {
    const currentValue = currentValues[key];
    const savedValue = savedFilters[key];
    const defaultValue = defaults[key];

    if (savedValue !== undefined) {
      return deepEqualWithDefaultDateHandling(currentValue, savedValue);
    }
    return deepEqualWithDefaultDateHandling(currentValue, defaultValue);
  });
};

export const isFilterAtDefault = (filterKey: keyof AdvancedFilterData, currentValue: unknown): boolean => {
  const defaults = getDefaultFilterValues();
  const defaultValue = defaults[filterKey];

  return deepEqualWithDefaultDateHandling(currentValue, defaultValue);
};

export const areAllFiltersAtDefaults = (currentValues: AdvancedFilterData): boolean => {
  const defaults = getDefaultFilterValues();

  return (Object.keys(defaults) as (keyof AdvancedFilterData)[]).every(key => {
    const currentValue = currentValues[key];
    const defaultValue = defaults[key];

    return deepEqualWithDefaultDateHandling(currentValue, defaultValue);
  });
};

export const hasConflictingFilters = (
  currentValues: AdvancedFilterData,
  filterSetFilters: PartialFilterData
): boolean => {
  const defaults = getDefaultFilterValues();

  return Object.keys(filterSetFilters).some(key => {
    const typedKey = key as keyof AdvancedFilterData;
    const currentValue = currentValues[typedKey];
    const defaultValue = defaults[typedKey];
    const filterSetValue = filterSetFilters[typedKey];

    return (
      filterSetValue !== undefined &&
      !deepEqualWithDefaultDateHandling(currentValue, defaultValue) &&
      !deepEqualWithDefaultDateHandling(currentValue, filterSetValue)
    );
  });
};

export const mergeFilterSet = (
  currentValues: AdvancedFilterData,
  filterSetFilters: PartialFilterData
): AdvancedFilterData => {
  const defaults = getDefaultFilterValues();
  const mergedValues = { ...currentValues };

  Object.entries(filterSetFilters).forEach(([key, value]) => {
    const typedKey = key as keyof AdvancedFilterData;
    const currentValue = currentValues[typedKey];
    const defaultValue = defaults[typedKey];

    if (value !== undefined && deepEqualWithDefaultDateHandling(currentValue, defaultValue)) {
      (mergedValues as Record<string, unknown>)[typedKey] = value;
    }
  });

  return mergedValues;
};

export const replaceWithFilterSet = (
  currentValues: AdvancedFilterData,
  filterSetFilters: PartialFilterData
): AdvancedFilterData => {
  const mergedValues = { ...currentValues };

  Object.entries(filterSetFilters).forEach(([key, value]) => {
    const typedKey = key as keyof AdvancedFilterData;
    if (value !== undefined) {
      (mergedValues as Record<string, unknown>)[typedKey] = value;
    }
  });

  return mergedValues;
};

export const resetAndApplyFilterSet = (filterSetFilters: PartialFilterData): AdvancedFilterData => {
  const defaults = getDefaultFilterValues();

  return mergeWithCurrentFilters(defaults, filterSetFilters);
};
